import React, { useState, useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import { Grid, Box, Typography, ThemeProvider } from "@mui/material";
import { basicTheme } from "../../themes/customTheme";
import TopFilterBar from "../../components/reusableComponents/TopFilterBar";
import DesktopTable from "../../components/reusableComponents/DesktopTable";
import { useDispatch, useSelector } from "react-redux";
import { getEachPartnerData } from "../../utils";
import AddPartnerModal from "../../components/partner/addPartnerModal";
import { CSVLink } from "react-csv";
import LinearProgress from "@mui/material/LinearProgress";
import useMediaQuery from "@mui/material/useMediaQuery";
import SnackBars from "../../components/reusableComponents/SnackBar";
import EditPartnerModal from "../../components/partner/editPartnerModal";
import DeletePartner from "../../components/partner/deletePartnerModal";
import AlertDialog from "../../components/reusableComponents/AlertDialog";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { useLocation } from "react-router-dom";
import { getPartnerData } from "./partnerAPIs";
import SuccessErrorDialogBox from "../../components/reusableComponents/SuccessErrorDialogBox";

const MainGrid = styled(Grid)({
  flex: "1",
  backgroundColor: "white",
  marginTop: "80px",
});

const initialHeadCells = [
  {
    key: "id",
    label: "ID",
    type: "text",
  },
  {
    key: "username",
    label: "Username",
    type: "text",
    capital: false,
  },
  {
    key: "email",
    label: "Email",
    type: "text",
    capital: false,
  },
  {
    key: "groups",
    label: "Groups",
    type: "text",
    capital: false,
  },
  {
    key: "action",
    label: "Actions",
    type: "editDelete",
    sort: false,
    sticky: true,
  },
];

export default function Partner({}) {
  const location = useLocation();
  const status = new URLSearchParams(location.search).get("status");
  const dispatch = useDispatch();
  const csvLink = useRef();
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const rowsPerPage = 10;
  const [openAddModal, setopenAddModal] = React.useState(false);
  const [offset, setOffset] = useState(0);
  const [page, setPage] = React.useState(0);
  const [sortingType, setSortingType] = useState("desc");
  const [sortBy, setSortBy] = useState("created_at");
  const [applyFilter, setApplyFilter] = useState(false);
  const [searchBarData, setSearchBarData] = useState("");
  const [searchBarText, setSeachBarText] = useState("");
  const [exportData, setExportData] = useState("");
  const [exportedFileName, setExportedFileName] = useState("");
  const [partnerSnackBarType, setPartnerSnackBarType] = useState("success");
  const [partnerSnackBarMessage, setPartnerSnackBarMessage] = useState("");
  const [showPartnerSnackBar, setPartnerShowSnackBar] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openEditModal, setopenEditModal] = useState(false);
  const [openEditModalWithLocation, setopenEditModalWithLocation] =
    useState(false);
  const [editData, setEditData] = useState({});
  const [editDataWithLocation, setEditDataWithLocation] = useState({});
  const [deleteData, setDeleteData] = useState({});
  const [reload, setReload] = useState(false);
  const [filters, setFilters] = useState([]);
  const [apiCall, setApiCall] = useState(false);
  const [openLocationSubModal, setOpenLocationSubModal] = React.useState(false);
  const [openFilterPopOver, setOpenFilterPopOver] = React.useState(null);
  const [openAlertModal, setOpenAlertModal] = useState(false);
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [clearFilter, setClearFilter] = useState(false);
  const [partnerDataResponse, setPartnerDataResponse] = useState([]);
  const [totalPartnerData, setTotalPartnerData] = useState(-1);
  const [loading, setLoading] = useState(false);
  const loggedInUser = JSON.parse(window.localStorage.getItem("loggedInUser"));
  const ADMIN_PARTNER = process.env.REACT_APP_ADMIN_PARTNER;
  const [openSuccessBox, setOpenSuccessBox] = useState(false);
  const [openSuccessBoxType, setOpenSuccessBoxType] = useState("success");
  const [successMessage, setSuccessMessage] = useState("");
  const [userCanEdit, setUserCanEdit] = useState(true);
  const [userCanDelete, setUserCanDelete] = useState(false);

  const handleShowFilterPopOver = (event) => {
    setOpenFilterPopOver(event.currentTarget);
  };

  const handleCloseFilterPopOver = () => {
    setOpenFilterPopOver(null);
    setApplyFilter(false);
    setClearFilter(false);
  };

  const drawerWidth = useSelector(
    (state) => state.appBarSliceReducer.drawerWidth
  );

  useEffect(() => {
    if (clearFilter === true) {
      setSelectedStates([]);
      setSelectedProjects([]);
      setPage(0);
      setOffset(0);
      setApiCall(true);
    }
  }, [clearFilter]);

  useEffect(() => {
    setPage(0);
    setOffset(0);
  }, [searchBarData]);

  useEffect(() => {
    if (applyFilter) {
      handleCloseFilterPopOver();
      setPage(0);
      setOffset(0);
    }
  }, [applyFilter]);

  useEffect(() => {
    setApiCall(true);
  }, [searchBarData, offset, sortingType, sortBy, applyFilter]);

  useEffect(() => {
    if (editData.id !== undefined && editData.id.toString() === ADMIN_PARTNER) {
      setopenEditModal(false);
    } else if (openEditModal) {
      setLoading(true);
      getEachPartnerData(editData.id)
        .then((response) => {
          if (response.status === 200) {
            setEditDataWithLocation(response.data);
            setopenEditModalWithLocation(true);
          } else {
            const errorDetail = response?.data?.response?.data?.detail || "Error fetching partner data";
            setPartnerSnackBarMessage(errorDetail);
            setPartnerSnackBarType("error"); 
            setPartnerShowSnackBar(true);
          }
        })
        .catch((error) => {
          console.log(error);
          // Get error message from response if available
          const errorMessage = error?.response?.data || "Something went wrong on Partner server side";
          setPartnerSnackBarMessage(errorMessage);
          setPartnerSnackBarType("error");
          setPartnerShowSnackBar(true);
        })
        .finally(() => {
          setLoading(false);
          setopenEditModal(false);
        });
    }
  }, [openEditModal]);

  useEffect(() => {
    if (apiCall || reload) {
      const params = {
        search: searchBarData,
        offset: offset,
        limit: rowsPerPage,
        // sort_key: sortBy,
        sort_by: "username",
      };
      if (selectedStates.length !== 0) {
        params["state_ids"] = [];
        selectedStates.forEach((location) => {
          params["state_ids"].push(location.id);
        });
      }
      if (selectedProjects.length !== 0) {
        params["project_ids"] = [];
        selectedProjects.forEach((project) => {
          params["project_ids"].push(project.id);
        });
      }

      const fetchData = async () => {
        try {
          setLoading(true);
          const response = await getPartnerData(params);
          if (
            response != null &&
            response != undefined &&
            response.data !== null &&
            response.data !== undefined &&
            response.data.results !== null &&
            response.data.results !== undefined
          ) {
            setTotalPartnerData(response.data.count);
            setPartnerDataResponse(response.data.results);
          } else {
            setPartnerSnackBarMessage("Something went wrong on sever!");
            setPartnerSnackBarType("error");
            setPartnerShowSnackBar(true);
          }
        } catch (error) {
          setPartnerSnackBarType("error");
          setPartnerSnackBarMessage(error.message);
          setPartnerShowSnackBar(true);
          console.error("API Error:", error);
        } finally {
          setLoading(false);
          setApiCall(false);
          setReload(false);
        }
      };

      fetchData();
    }
  }, [apiCall, reload]);

  useEffect(() => {
    if (partnerSnackBarMessage !== "" && partnerSnackBarMessage !== undefined)
      setPartnerShowSnackBar(true);
  }, [partnerSnackBarMessage]);

  useEffect(() => {
    const loggedInUser = JSON.parse(window.localStorage.getItem("loggedInUser"));
    const isAdmin = loggedInUser?.role?.includes("Admin");
    setUserCanEdit(isAdmin);
    setUserCanDelete(isAdmin);
  }, []);

  const handlePartnerDeletion = (deletedPartnerId) => {
    setPartnerDataResponse((prevData) =>
      prevData.filter((partner) => partner.id !== deletedPartnerId)
    );
    setTotalPartnerData((prevCount) => prevCount - 1);
  };

  return (
    <MainGrid
      container
      id="main"
      sx={{ marginLeft: drawerWidth + "px", width: "auto" }}
      spacing={3}
    >
      <ThemeProvider theme={basicTheme}>
        <Grid item xs={12}>
          <TopFilterBar
            addtext="Add User"
            setAddOpen={setopenAddModal}
            searchBarData={searchBarData}
            setSearchBarData={setSearchBarData}
            searchBarText={searchBarText}
            setSeachBarText={setSeachBarText}
            handleShowFilterPopOver={handleShowFilterPopOver}
            setExportData={setExportData}
            searchBy="Search By User Name"
            loading={loading}
            // showFilter={loggedInUser.role.map(role => role.toLowerCase()).includes("admin")}
            filterSelected={
              selectedStates.length !== 0 || selectedProjects.length !== 0
            }
          />
        </Grid>
        <Grid item xs={12}>
          {loading && (
            <Box>
              <LinearProgress color="primary" />
            </Box>
          )}
          {!loading && (
            <DesktopTable
              key={partnerDataResponse}
              headCells={initialHeadCells}
              // tableResponse={partnerDataResponse}
              tableResponse={partnerDataResponse.map((item) => ({
                id: item.id,
                username: item.user.username,
                email: item.user.email,
                groups: item.user.groups.map((group) => group.name).join(", "), 

              }))}
              totalCount={totalPartnerData}
              setOffset={setOffset}
              rowsPerPage={rowsPerPage}
              setSortingType={setSortingType}
              sortingType={sortingType}
              setSortBy={setSortBy}
              sortBy={sortBy}
              setopenEditModal={setopenEditModal}
              setEditData={setEditData}
              setOpenDeleteModal={setOpenDeleteModal}
              setDeleteData={setDeleteData}
              setPage={setPage}
              page={page}
              setOpenLocationSubModal={setOpenLocationSubModal}
              openEditModal={openEditModal}
              openDeleteModal={openDeleteModal}
              canEdit={userCanEdit}
              canDelete={userCanDelete}
            />
          )}
        </Grid>
        {openEditModalWithLocation && (
          <EditPartnerModal
            openModal={openEditModalWithLocation}
            handleClose={() => setopenEditModalWithLocation(false)}
            data={editDataWithLocation}
            setEditData={setEditDataWithLocation}
            setPartnerSnackBarMessage={setPartnerSnackBarMessage}
            setPartnerSnackBarType={setPartnerSnackBarType}
            setReload={setReload}
            setOpenSuccessBox={setOpenSuccessBox}
            setSuccessMessage={setSuccessMessage}
            setOpenSuccessBoxType={setOpenSuccessBoxType}
          />
        )}
        {openAddModal && (
          <AddPartnerModal
            openAddModal={openAddModal}
            handleClose={() => setopenAddModal(false)}
            setPartnerSnackBarMessage={setPartnerSnackBarMessage}
            setPartnerSnackBarType={setPartnerSnackBarType}
            setReload={setReload}
            setOpenSuccessBox={setOpenSuccessBox}
            setSuccessMessage={setSuccessMessage}
            setOpenSuccessBoxType={setOpenSuccessBoxType}
          />
        )}
        {openSuccessBox && (
          <SuccessErrorDialogBox
            open={openSuccessBox}
            handleClose={() => setOpenSuccessBox(false)}
            text={successMessage}
            type={openSuccessBoxType}
          />
        )}

        {openDeleteModal && (
          <DeletePartner
            handleClose={() => setOpenDeleteModal(false)}
            open={openDeleteModal}
            deleteData={deleteData}
            setSnackBarMessage={setPartnerSnackBarMessage}
            setReload={setReload}
            setOpenAlertModal={setOpenAlertModal}
            onDeleteSuccess={handlePartnerDeletion}
          />
        )}
        {/* <CSVLink
          data={exportedData}
          filename={exportedFileName}
          className="hidden"
          ref={csvLink}
          target="_blank"
          separator={";"}
        /> */}
        {showPartnerSnackBar && (
          <SnackBars
            open={showPartnerSnackBar}
            handleClose={() => {
              setPartnerShowSnackBar(false);
              setPartnerSnackBarMessage("");
            }}
            severity={partnerSnackBarType}
            message={partnerSnackBarMessage}
            autoHideDuration={6000}
            vertical="bottom"
            horizontal="center"
          />
        )}
        {loading && (
          <Backdrop
            sx={{
              color: "#fff",
              position: "absolute",
              zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
            open={loading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
      </ThemeProvider>
    </MainGrid>
  );
}
